import type { GetServerSideProps } from 'next';
import type { PageProps } from 'lib/queries/pages';
import type { Page } from 'types/page';
import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import WebPageJson from 'components/structured-data/webpage';
import ProductJson from 'components/structured-data/product';
import PageWrap from 'components/page-wrap';
import Blocks from 'components/blocks';

import { getPageServerSide } from 'lib/queries/pages';
import { parse } from 'cookie';
import { POSTHOG_COOKIE_KEY } from 'lib/cookies/posthog-cookie';
import { default as posthogAPI } from 'lib/posthog';
import { usePostHog } from 'posthog-js/react';
import camelcaseKeys from 'camelcase-keys';
import StickyMobileCta from 'components/sticky-mobile-cta';
import MedicalReviewer from 'components/medical-reviewer';
import { getPostHogServerClient } from 'lib/posthog-server';

const HeroCard = dynamic(() => import('components/hero-card'));
const HeroCardNew = dynamic(() => import('components/hero-card-new'));
const Loader = dynamic(() => import('components/loader'));

const staticPage = [
  {
    id: 81487,
    date: '2023-06-26T03:33:40',
    modified: '2026-04-09T07:58:44',
    slug: 'louisiana',
    title: 'Get a Louisiana (LA) Medical Marijuana Card Online',
    content: '',
    acf: {
      city: '',
      page_type: 'card',
      has_referral: true,
      hero: {
        title: 'Get a Louisiana Medical Marijuana Card',
        rich_title: '',
        renew_title: '',
        sub_title: 'Get Approved in Minutes',
        form_id: '',
        form_type: 'partner',
        form_title: '',
        form_label: '',
        renew_form_content_items: false,
        form_submit_text: 'Get Started',
        form_submit_url: '',
        address: '',
        description:
          '<p>See a LA-licensed medical marijuana doctor online in minutes and get the cannabis care you deserve.</p>\n',
        renew_description: '',
        image: null,
        image_alignment: 'right',
        steps: false,
        button: null,
        download_button: null,
        badges: [
          {
            label: '',
            icon: null,
          },
          {
            label: '',
            icon: null,
          },
          {
            label: '',
            icon: null,
          },
        ],
        google_my_business_details: {
          medical_card_utm: '',
          cannabis_counseling_services_utm: '',
          phone_number: '',
        },
        webinar_members: false,
        quick_links: [
          {
            section: 'qualifying-conditions',
            title: 'Do I qualify? ',
            description: 'Check the qualifying conditions within seconds.',
            icon: {
              id: 95920,
              src: 'https://admin.leafwell.com/app/uploads/2025/11/icon-qualify.svg',
              width: 1,
              height: 1,
              srcSet: false,
              alt: '',
            },
          },
          {
            section: 'cost-information',
            title: 'Affordable price',
            description: 'Risk-free process. Get approved or your money back.',
            icon: {
              id: 95921,
              src: 'https://admin.leafwell.com/app/uploads/2025/11/icon-affordable.svg',
              width: 1,
              height: 1,
              srcSet: false,
              alt: '',
            },
          },
          {
            section: 'quick-steps',
            title: 'Quick, easy process',
            description:
              'Book an appointment, consult with a doctor, and receive your certification in just a few minutes.',
            icon: {
              id: 95922,
              src: 'https://admin.leafwell.com/app/uploads/2025/11/icon-quick-easy.svg',
              width: 1,
              height: 1,
              srcSet: false,
              alt: '',
            },
          },
        ],
        latitude: '',
        longitude: '',
        opening_hours: false,
      },
      linktree: {
        categories: false,
      },
      redirect_medical_card: true,
      redirect_product_endpoint: '',
      custom_state_code: '',
      pause_appointments: false,
      is_affiliate: false,
      affiliate_logo: null,
      affiliate_website: '',
      form_url: '',
      vpd_page: [],
      is_experiment: false,
      is_renewal: false,
      is_clinic: false,
      total_medical_card_price: '79',
      name: 'Get a Louisiana (LA) Medical Marijuana Card Online',
      description:
        'Get your LA medical marijuana card online for just $79. Connect with a Leafwell healthcare provider now and get certified fast!',
      image_object_caption: 'Louisiana (LA) Medical Marijuana Card by Leafwell',
      product: {
        name: 'Louisiana Medical Marijuana Card',
        description:
          'A Louisiana medical marijuana card from Leafwell allows you to purchase cannabis legally from dispensaries throughout LA.',
        image_url: {
          id: 74476,
          src: 'https://admin.leafwell.com/app/uploads/2024/01/LA.jpg',
          width: 420,
          height: 428,
          srcSet:
            'https://admin.leafwell.com/app/uploads/2024/01/LA.jpg 420w, https://admin.leafwell.com/app/uploads/2024/01/LA-393x400.jpg 393w',
          alt: 'LA card',
        },
      },
      state_product_image: null,
      is_vpd_landing: false,
      vpd_logo: null,
      price: {
        price: '',
        new_price: '',
        percentage_discount: '',
        discount_code: '',
        free_discount_code: '',
      },
      patient_renewal_dropdown: false,
      patient_renewal_dropdown_info:
        'Prior to your consultation, we will verify your status within the program',
      affiliate_page: [],
      webinar_info: {
        webinar_title: '',
        webinar_description: '',
        webinar_zoom_url: '',
      },
      vpd_partner_name: false,
      states: ['LA'],
      blocks: [
        {
          acf_fc_layout: 'benefits_of_mmc',
          instance_id: 'f023f780',
          section_id: 'key-benefits-of-mmc',
          title: 'The key benefits of a medical marijuana card in Louisiana',
          description: '',
          access_to_dispensaries: {
            highlight: true,
            title: 'Access to dispensaries',
            description:
              '<p>Having a medical card is the only way to legally purchase, use, and possess cannabis in the state of Louisiana.</p>\n<p>Recreational use is illegal.</p>\n',
          },
          tax_savings: {
            highlight: false,
            title: 'Tax savings',
            description: '',
          },
          cultivation: {
            highlight: false,
            title: 'Cultivation',
            description: '',
          },
          higher_possession: {
            highlight: false,
            title: 'Higher possession',
            description:
              '<p>Medical card holders can <strong>possess a 30 day supply of cannabis products</strong>.</p>\n<p>Non-card holders cannot legally possess cannabis products.</p>\n',
          },
          access_to_products: {
            highlight: false,
            title: 'Access <strong>higher-potency</strong> cannabis products',
            description: '',
          },
          lower_age_restriction: {
            highlight: false,
            title:
              'Get legal cannabis <strong>at age 18+</strong>, rather than 21+',
            description:
              '<p>Patients 18+ can qualify for a medical card.</p>\n<p>Minors can also apply for a medical card if they have an approved caregiver.</p>\n',
          },
          legal_protection: {
            highlight: false,
            title: 'Enjoy <strong>legal protections</strong> for employment',
            description: '',
          },
        },
        {
          acf_fc_layout: 'reviews',
          instance_id: '0ebab0ad',
          section_id: 'testimonials',
          title: 'Trusted by 1 Million Monthly Users',
          intro: '',
          trustpilot: true,
          show_trust_signals: false,
          variant: 'full',
          quoted: false,
        },
        {
          acf_fc_layout: 'cards_image',
          instance_id: '51a8f151',
          section_id: 'quick-steps',
          title: '3 easy steps to get your LA medical card',
          label: 'Get approved in minutes using Leafwell’s online platform.',
          label_position: 'below_title',
          list_numbered: true,
          cards_background_color: 'beige',
          cards: [
            {
              title: 'Sign up',
              description:
                '<p>Register with Leafwell on any device and consult with a licensed medical marijuana doctor in minutes.</p>\n',
              image: {
                id: 96266,
                src: 'https://admin.leafwell.com/app/uploads/2025/12/icon-signup-yellow.svg',
                width: 1,
                height: 1,
                srcSet: false,
                alt: 'sign up icon',
              },
            },
            {
              title: 'Get approved',
              description:
                '<p>Once approved, your doctor will fax your certification to your chosen pharmacy.</p>\n',
              image: {
                id: 96267,
                src: 'https://admin.leafwell.com/app/uploads/2025/12/icon-certificate-purple.svg',
                width: 1,
                height: 1,
                srcSet: false,
                alt: 'certificate',
              },
            },
            {
              title: 'Shop products',
              description:
                '<p>Show your ID at your chosen pharmacy to shop products.</p>\n',
              image: {
                id: 96279,
                src: 'https://admin.leafwell.com/app/uploads/2025/12/icon-buy-products-aqua.svg',
                width: 1,
                height: 1,
                srcSet: false,
                alt: '',
              },
            },
          ],
          description: '',
          button: '',
        },
        {
          acf_fc_layout: 'youtube_video',
          instance_id: '4183def3',
          section_id: '',
          title: 'A few more questions about the process?',
          content:
            'Watch this video to learn more about getting a medical marijuana card in Louisiana.',
          variant: 'none',
          video_size: 'small',
          youtube_videos: [
            {
              youtube_video_id: 'iNVd1ZeCTj8',
              image: null,
              play_button_style: 'secondary',
            },
          ],
        },
        {
          acf_fc_layout: 'table',
          instance_id: 'b841b5ae',
          section_id: 'cost-information',
          display_product_state_image: false,
          title: 'How much does the LA card cost?',
          title_tag: 'h3',
          intro_text:
            '<p>Louisiana medical marijuana patients save more with Leafwell. Get your money back if not approved.</p>\n',
          table: {
            columns: [
              {
                column_type: 'values',
                label: '',
                values: [
                  {
                    value_text: 'Medical marijuana certificate',
                    value_check: false,
                  },
                  {
                    value_text: 'State application fee',
                    value_check: false,
                  },
                  {
                    value_text: 'Total',
                    value_check: false,
                  },
                ],
              },
              {
                column_type: 'values',
                label: 'New with Leafwell',
                values: [
                  {
                    value_text: '$79',
                    value_check: false,
                  },
                  {
                    value_text: '$0',
                    value_check: false,
                  },
                  {
                    value_text: '$79',
                    value_check: false,
                  },
                ],
              },
              {
                column_type: 'values',
                label: 'Renewal with Leafwell',
                values: [
                  {
                    value_text: '$79',
                    value_check: false,
                  },
                  {
                    value_text: '$0',
                    value_check: false,
                  },
                  {
                    value_text: '$79',
                    value_check: false,
                  },
                ],
              },
            ],
          },
          button: {
            title: 'Apply Now',
            url: '#leafwell-signup-form',
            target: '',
          },
        },
        {
          acf_fc_layout: 'content_and_banner',
          instance_id: 'e9bc2fe4',
          section_id: 'qualifying-conditions',
          title: 'How to qualify for your MMJ card in Louisiana',
          background_color: 'beige',
          content_left:
            '<p>Adults at least 18 experiencing at least one qualifying condition may be eligible to apply for a Louisiana medical marijuana card. All minor patients must have the consent of a parent or legal guardian to be eligible for a medical marijuana card in Louisiana. Qualifying medical conditions in LA include:</p>\n<ul>\n<li><a href="https://leafwell.com/medical-conditions/alzheimers-disease">Alzheimer’s Disease</a></li>\n<li><a href="https://leafwell.com/medical-conditions/lou-gehrigs-disease-aka-amyotrophic-lateral-sclerosis-als">Amyotrophic Lateral Sclerosis (ALS)</a>, aka Lou Gehrig’s Disease</li>\n<li><a href="https://leafwell.com/medical-conditions/autism-asperger-syndrome">Autism Spectrum Disorder</a> (Self-injuring behavior, physically aggressive or destructive behavior, avoidance/inability to communicate to where the physical health is jeopardized)</li>\n<li><a href="https://leafwell.com/medical-conditions/cachexia-or-wasting-syndrome">Cachexia or Wasting Syndrome</a></li>\n<li><a href="https://leafwell.com/medical-conditions/cancer">Cancer</a></li>\n<li>Concussion diagnosed by an authorized clinician</li>\n<li><a href="https://leafwell.com/medical-conditions/crohns-disease">Crohn’s Disease</a></li>\n<li><a href="https://leafwell.com/medical-conditions/chronic-pain-muscle-pain">Chronic Pain</a></li>\n<li><a href="https://leafwell.com/medical-conditions/epilepsy">Epilepsy</a></li>\n<li><a href="https://leafwell.com/medical-conditions/glaucoma">Glaucoma</a></li>\n<li><a href="https://leafwell.com/medical-conditions/hiv-aids">HIV/AIDS</a></li>\n</ul>\n',
          content_right:
            '<ul>\n<li><a href="https://leafwell.com/medical-conditions/huntingtons-disease-hd">Huntington’s Disease</a></li>\n<li>Intractable Pain</li>\n<li><a href="https://leafwell.com/medical-conditions/lewy-body-dementia-lbd">Lewy Body Dementia</a></li>\n<li>Motor Neuron Disease</li>\n<li><a href="https://leafwell.com/medical-conditions/muscular-dystrophy-md-myotonic-disorders">Muscular Dystrophy (MD)</a></li>\n<li><a href="https://leafwell.com/medical-conditions/multiple-sclerosis">Multiple Sclerosis (MS)</a></li>\n<li><a href="https://leafwell.com/medical-conditions/palliative-care-admittance-into-hospice">Parkinson’s Disease (PD)</a></li>\n<li><a href="https://leafwell.com/medical-conditions/post-traumatic-stress-disorder-ptsd">Post-Traumatic Stress Disorder (PTSD)</a></li>\n<li><a href="https://leafwell.com/medical-conditions/seizures">Seizure Disorders</a></li>\n<li><a href="https://leafwell.com/medical-conditions/muscle-spasms">Severe Muscle Spasms</a></li>\n<li>Spasticity</li>\n<li>Spinal Muscular Atrophy</li>\n<li><a href="https://leafwell.com/medical-conditions/stroke-traumatic-brain-injury">Traumatic Brain Injury</a></li>\n<li>Any condition where a patient is receiving hospice care or palliative care</li>\n<li>Any condition a provider considers debilitating to a patient through their medical education and training</li>\n</ul>\n',
          banner_title: 'Convenient, Quality Care When You Need It Most',
          banner_label:
            "With Leafwell, you'll be matched up with a highly knowledgeable, empathetic, licensed medical marijuana doctor or provider.",
          banner_image: {
            id: 76039,
            src: 'https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors.png',
            width: 1568,
            height: 750,
            srcSet:
              'https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-700x335.png 700w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-1024x490.png 1024w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-400x191.png 400w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-768x367.png 768w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-1536x735.png 1536w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors.png 1568w',
            alt: 'health care providers',
          },
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: 'a9934d79',
          container_size: 'large',
          content:
            '<h2>2 documents needed to apply for a Louisiana medical marijuana card</h2>\n<p>You&#8217;ll need to provide the following to apply for a medical marijuana card in Louisiana:</p>\n<ol>\n<li>Medical Marijuana Recommendation<br />\nThis will be faxed to your local pharmacy upon approval — you don&#8217;t need to apply to the state.</li>\n<li>Proof of Identity<br />\nDriver&#8217;s License or State ID.</li>\n</ol>\n<div data-highlight="button" data-label="Get Your Card" data-ref="#leafwell-signup-form" data-target="_self"></div>\n',
          section_id: '',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '7b481091',
          container_size: 'default',
          content:
            '<h2>Next steps after approval</h2>\n<p>You don’t need to do anything! Louisiana does not offer a medical marijuana card so there’s no state paperwork.</p>\n<ol>\n<li>Leafwell will fax your certificate to your <strong>pharmacy of choice</strong>. You can find a list of pharmacies <a href="https://leafwell.com/medical-card/louisiana?knowledgebase_id=pharmacies">here</a>.</li>\n<li>As soon as the pharmacy receives and processes your certificate from us, you can legally purchase medical cannabis products there*. Ensure you bring a valid form of photo identification, such as a driver’s license, with you to the pharmacy.</li>\n</ol>\n<p><strong>*Please note:</strong> While some pharmacies are able to process new patients in a few hours, others may take up to 5 days.</p>\n<h4><strong>Pharmacy Changes</strong></h4>\n<p>After becoming a patient, if you want to change pharmacies by having your recommendation transferred, simply reach out to the NEW pharmacy of your choice. They can initiate the necessary process.</p>\n',
          section_id: 'state-steps',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '49007a02',
          container_size: 'default',
          content:
            '<h2>How to renew your card</h2>\n<p>The LA medical certification renewal process is similar to getting certified for medical cannabis. You will need to speak with a healthcare provider and verify that you still have one or more qualifying conditions.</p>\n<p>Renewing with the same provider who provided your initial certification might be the fastest and easiest way, as they may still have your information on file. Leafwell can help you complete your renewal process.</p>\n',
          section_id: 'renew',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: 'dc188860',
          container_size: 'default',
          content:
            '<h2>Legal limitations for LA card holders</h2>\n<p>Navigating the legal landscape of medical marijuana can be a bit tricky, but we&#8217;re here to break it down for you. Here are some key points you should be aware of if you&#8217;re a medical cannabis patient in Louisiana:</p>\n<ul>\n<li aria-level="1">Adults 18 and older with a valid medical marijuana certificate from a licensed healthcare provider can purchase up to a 30-day supply of<a href="https://leafwell.com/blog/category/cannabis-products" target="_blank" rel="noopener"> medical marijuana products</a>, with the final amount to be approved by the prescribing provider.</li>\n<li aria-level="1">Patients are also permitted to possess up to 2.5 ounces of <a href="https://leafwell.com/blog/what-cannabis-flower">cannabis flower</a> per 14-day period from a licensed dispensary.</li>\n<li aria-level="1">No Recreational Use: Just a heads-up—recreational marijuana is still a no-go in Louisiana. Your medical cannabis card doesn&#8217;t give you a free pass for recreational use.</li>\n<li aria-level="1">Possession of less than 14 grams of cannabis has been <a href="https://leafwell.com/blog/decriminalization-vs-legalization">decriminalized</a> and is punishable by a $100 fine; however, first offenders caught with more than 14 grams of marijuana face a fine of up to $500 and up to six months of imprisonment. <a href="https://legis.la.gov/Legis/law.aspx?d=98880">Penalties</a> for subsequent offenses come with greater fines and longer jail time.</li>\n<li aria-level="1">Cultivation is Off-Limits: Even if you&#8217;re a medical marijuana patient, growing your own cannabis plants is illegal in Louisiana.</li>\n<li aria-level="1">Despite recreational marijuana use being illegal in Louisiana, adults 18 and older can purchase hemp-derived CBD that contains less than <a href="https://leafwell.com/blog/03-thc-meaning">0.3% THC</a>. THC isomers such as delta-8 and delta-9 are also legal for adult use.</li>\n</ul>\n',
          section_id: 'laws',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '8eeed485',
          container_size: 'default',
          content:
            '<h2>How to become a caregiver in Louisiana</h2>\n<p>Minors under 18 can qualify for a Louisiana medical marijuana certification with consent from a parent or legal guardian who will serve as their <a href="https://faq.leafwell.com/knowledge/louisiana-caregiver">caregiver</a>.</p>\n<p>For minors with a<a href="https://leafwell.com/medical-conditions/autism-asperger-syndrome">utism spectrum disorder (ASD)</a>, the certifying provider must consult with the patient&#8217;s pediatric sub-specialist before issuing the recommendation.</p>\n<p>Louisiana does not require state registration for patients and caregivers. Parents and guardians who are primary caregivers can contact the patient&#8217;s pharmacy directly to ensure the patient&#8217;s recommendation is processed correctly.</p>\n',
          section_id: 'caregiver',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '18486e7e',
          container_size: 'default',
          content:
            '<h2>Can you use a Louisiana medical card in other states?</h2>\n<p>Louisiana gives legal protections to verified out-of-state patients and allows those patients to purchase medical marijuana products from the state’s dispensaries.</p>\n<p>Several other states also practice medical marijuana reciprocity and may recognize a Louisiana patient’s status as such.</p>\n<p>However, you may need to request a physical copy of your certification to benefit from medical reciprocity.</p>\n<p>Other states that recognize out-of-state medical marijuana cards include:</p>\n<ul>\n<li aria-level="1">Arizona</li>\n<li aria-level="1">Arkansas *</li>\n<li aria-level="1">Delaware</li>\n<li aria-level="1">Hawaii *</li>\n<li aria-level="1">Maine</li>\n<li aria-level="1">Michigan (at the dispensary’s discretion)</li>\n<li aria-level="1">Nevada</li>\n<li aria-level="1">New Hampshire</li>\n<li aria-level="1">New Jersey *</li>\n<li aria-level="1">New Mexico</li>\n<li aria-level="1">Oklahoma *</li>\n<li aria-level="1">Puerto Rico</li>\n<li aria-level="1">Rhode Island</li>\n<li aria-level="1">U.S. Virgin Islands *</li>\n<li aria-level="1">Utah * (up to two 21-day periods in a calendar year)</li>\n<li aria-level="1">Washington, D.C.</li>\n</ul>\n<p>*Visitors must complete a visiting patient application with the state program.</p>\n',
          section_id: 'reciprocity',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '6d010f76',
          container_size: 'default',
          content:
            '<h2>Minor medical cannabis patient information</h2>\n<p>Minors under 18 can qualify for a Louisiana medical marijuana certification with consent from a parent or legal guardian who will serve as their <a href="https://faq.leafwell.com/knowledge/louisiana-caregiver">caregiver</a>.</p>\n<p>An online consultation with a Leafwell healthcare provider costs $79. You will not be charged if your child is not approved.</p>\n<p>For minors with <a href="https://leafwell.com/medical-conditions/autism-asperger-syndrome">autism spectrum disorder (ASD)</a>, the certifying provider must consult with the patient&#8217;s pediatric sub-specialist before issuing the recommendation.</p>\n<p>Louisiana does not require state registration for patients and caregivers. Parents and guardians who are primary caregivers can contact the patient&#8217;s pharmacy directly to ensure the patient&#8217;s recommendation is processed correctly.</p>\n',
          section_id: 'minor',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: 'aa918cf2',
          container_size: 'default',
          content:
            '<h2>Do I get a physical card in Louisiana?</h2>\n<p>No, you don&#8217;t get a medical marijuana card — physical or electronic — in Louisiana.</p>\n<p>Instead, your healthcare provider will fax your recommendation to your chosen pharmacy for processing, which varies between a few hours to a few days, depending on the location. Once your recommendation has been processed, <strong>all you need is your ID for the pharmacy to verify your identity</strong>.</p>\n<p>For reference, you can view a copy of the faxed recommendation via your Leafwell account.</p>\n',
          section_id: 'card',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '3da9c3ea',
          container_size: 'default',
          content:
            '<h2>Louisiana medical marijuana pharmacies</h2>\n<ul>\n<li>Region 1 &#8211; Metropolitan\n<ul style="list-style-type: circle;">\n<li><a href="https://www.nolacannabisdispensary.com/">NOLA Cannabis Co.</a> (Kenner) &#8211; formerly Crescent City Therapeutics</li>\n<li><a href="https://www.nolacannabisdispensary.com/">NOLA Cannabis Co.</a> (New Orleans) &#8211; formerly H&amp;W Dispensary</li>\n<li><a href="https://www.nolacannabisdispensary.com/">NOLA Cannabis Co.</a> (Metairie) &#8211; formerly H&amp;W Dispensary</li>\n</ul>\n</li>\n<li>Region 2 &#8211; Capitol\n<ul style="list-style-type: circle;">\n<li><a href="https://capitolpharmacy.com/">Capitol Wellness Solutions</a> (Baton Rouge)</li>\n<li><a href="https://capitolpharmacy.com/">Cookies by Capitol Wellness</a> (Baton Rouge)</li>\n<li><a href="https://capitolpharmacy.com/">Capitol Wellness Solutions</a> (Prairieville)</li>\n</ul>\n</li>\n<li>Region 3 &#8211; Teche\n<ul style="list-style-type: circle;">\n<li><a href="https://greenleaf-dispensary.com/">Green Leaf Dispensary</a> (Houma)</li>\n<li><a href="https://greenleaf-dispensary.com/">Green Leaf Dispensary</a> (Morgan City)</li>\n</ul>\n</li>\n<li>Region 4 &#8211; Acadiana\n<ul style="list-style-type: circle;">\n<li><a href="https://theapothecaryshoppe.net/">The Apothecary Shoppe</a> (Lafayette)</li>\n<li><a href="https://theapothecaryshoppe.net/">The Apothecary Shoppe</a> (New Iberia)</li>\n</ul>\n</li>\n<li>Region 5 &#8211; Southwest\n<ul style="list-style-type: circle;">\n<li><a href="https://gooddayfarmdispensary.com/good-day-farm-jennings/">Good Day Farm</a> (Jennings)</li>\n<li><a href="https://gooddayfarmdispensary.com/good-day-farm-lake-charles/">Good Day Farm</a> (Lake Charles)</li>\n<li><a href="https://gooddayfarmdispensary.com/good-day-farm-sulphur/">Good Day Farm</a> (Sulphur)</li>\n</ul>\n</li>\n<li>Region 6 &#8211; Central\n<ul style="list-style-type: circle;">\n<li><a href="https://themed.cab/">The Medicine Cabinet</a> (Alexandria)</li>\n<li><a href="https://themed.cab/">The Medicine Cabinet</a> (Leesville)</li>\n<li><a href="https://themed.cab/" target="_blank" rel="noopener">The Medicine Cabinet</a> (Marksville)</li>\n</ul>\n</li>\n<li>Region 7 &#8211; Northwest\n<ul style="list-style-type: circle;">\n<li><a href="https://gooddayfarmdispensary.com/good-day-farm-bossier-city/" target="_blank" rel="noopener">Good Day Farm</a> (Bossier City)</li>\n<li><a href="https://gooddayfarmdispensary.com/good-day-farm-natchitoches/">Good Day Farm</a> (Natchitoches)</li>\n<li><a href="https://gooddayfarmdispensary.com/good-day-farm-shreveport/">Good Day Farm &#8211; Kings Hwy</a> (Shreveport)</li>\n<li><a href="https://gooddayfarmdispensary.com/good-day-farm-shreveport-mansfield/" target="_blank" rel="noopener">Good Day Farm &#8211; Mansfield Rd</a> (Shreveport)</li>\n</ul>\n</li>\n<li>Region 8 &#8211; Northeast\n<ul style="list-style-type: circle;">\n<li><a href="https://storycannabis.com/dispensary-locations/louisiana/delta-medmar-monroe/">Story Cannabis</a> (Monroe) &#8211; formerly Delta MedMar</li>\n<li><a href="https://storycannabis.com/dispensary-locations/louisiana/delta-medmar-ruston/" target="_blank" rel="noopener">Story Cannabis</a> (Ruston) &#8211; formerly Delta MedMar</li>\n<li><a href="https://storycannabis.com/dispensary-locations/louisiana/west-monroe-la/" target="_blank" rel="noopener">Story Cannabis</a> (West Monroe)</li>\n</ul>\n</li>\n<li>Region 9 &#8211; Southeast\n<ul style="list-style-type: circle;">\n<li><a href="https://willowdispensary.com/">Willow Pharmacy</a> (Covington)</li>\n<li><a href="https://willowdispensary.com/" target="_blank" rel="noopener">Willow Pharmacy</a> (Hammond)</li>\n<li><a href="https://willowdispensary.com/">Willow Pharmacy</a> (Slidell)</li>\n</ul>\n</li>\n</ul>\n',
          section_id: 'pharmacies',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'faqs',
          instance_id: 'ce318819',
          section_id: 'faqs',
          title: 'FAQs',
          description:
            '<p>Find answers to commonly asked questions about medical cannabis in Louisiana.</p>\n',
          items: [
            {
              id: 81698,
              title:
                'Can I apply for my medical marijuana certificate online in Louisiana?',
              content:
                "Telemedicine for medical cannabis is legal in Louisiana. Therefore Leafwell's services are accessible via telehealth in Louisiana.",
            },
            {
              id: 81700,
              title: 'Can I renew my Louisiana medical cannabis card online?',
              content:
                "Yes, Leafwell can help you renew your medical marijuana certificate and medical card. If you're a returning patient, all your information is stored in our HIPAA-compliant platform, and the process is quick and easy.",
            },
            {
              id: 81702,
              title:
                'How often do I need to renew my Louisiana medical marijuana certificate?',
              content:
                'Louisiana medical marijuana recommendations are valid for one year and can be renewed online. We recommend renewing 7-10 days before your expiration date to avoid a lapse in coverage.',
            },
            {
              id: 81704,
              title: 'What are the medical marijuana laws in Louisiana?',
              content:
                '<ul>\r\n \t<li>1978 - Louisiana first <a href="https://libguides.law.lsu.edu/Cannabis">approved medical cannabis</a> for cancer and glaucoma patients.</li>\r\n \t<li>2015 - Louisiana passed the <a href="https://trackbill.com/bill/louisiana-senate-bill-143-pharmacists-enacts-alison-neustrom-act-and-provides-relative-to-prescribed-marijuana-for-therapeutic-uses-and-the-development-of-rules-and-regulations-by-the-louisiana-board-of-pharmacy-and-the-louisiana-state-board-of-medical-examiners-gov-sig/1170704/">Alison Neustrom Act (SB 143)</a> in 2015, which paved the way for medical marijuana legalization in the state.</li>\r\n \t<li>2018 - Medical marijuana is made available in dispensaries.</li>\r\n \t<li>2020 - <a href="https://www.legis.la.gov/legis/BillInfo.aspx?i=238772">HB 819</a> was signed into law by Governor John Bel Edwards.</li>\r\n</ul>',
            },
            {
              id: 81706,
              title:
                'Will I be added to a public database once I have my medical marijuana certificate in Louisiana?',
              content:
                'In order to legally be able to use cannabis in Louisiana, the pharmacy of your choice will submit your details to a closed database as part of the Louisiana medical marijuana program. The list is not available to the public.',
            },
            {
              id: 81708,
              title: 'Where can I buy medical cannabis in Louisiana?',
              content:
                'If you have a medical marijuana certificate, you can buy medical cannabis in Louisiana at a licensed pharmacy. You can choose one of over 20 licensed pharmacies to have your certificate sent to.',
            },
            {
              id: 81831,
              title:
                'Are employees with medical marijuana protected in Louisiana?',
              content:
                'In June 2022, <a href="https://www.legis.la.gov/legis/BillInfo.aspx?i=243102">HB 988</a> was signed into law. Its provisions provide employment protections for certain state employees who possess a valid medical marijuana certificate. The law does not apply to personnel in the fields of:\r\n<ul>\r\n \t<li>Emergency medical services</li>\r\n \t<li>Firefighter services</li>\r\n \t<li>Law enforcement</li>\r\n \t<li>Public safety (officials)</li>\r\n \t<li>The Horse Racing Commission</li>\r\n \t<li>While this bill protects those who test positive for cannabis, it does not apply to individuals who are intoxicated on the job.</li>\r\n</ul>\r\nThe Louisiana Marijuana Task Force has drafted legislation that would extend the same protections to employees in the private sector. In May 2023, lawmakers advanced <a href="https://legis.la.gov/legis/BillInfo.aspx?i=244249">HB 351</a>, which aims to protect medical cannabis patients from losing unemployment benefits due to legal cannabis use.',
            },
            {
              id: 83739,
              title:
                'Does a medical marijuana card show up on a background check in Louisiana?',
              content:
                'No. Having a Louisiana medical cannabis card will not show up on a federal background check.',
            },
            {
              id: 83741,
              title:
                'Can I own a gun if I have a medical marijuana card in Louisiana?',
              content:
                'No. Federal law prohibits any marijuana users - including medical patients - from owning firearms. Louisiana does not have any specific language suggesting otherwise at this moment in time.',
            },
            {
              id: 83743,
              title: 'Can a felon get a medical marijuana card in Louisiana?',
              content:
                'Yes. If you have one or more qualifying conditions, you can get certified for medical cannabis in Louisiana, even with a felony.',
            },
            {
              id: 83745,
              title: 'Does HIPAA apply to medical marijuana?',
              content:
                'Yes. Those who have access to any level of medical cannabis patient information are required to follow HIPAA (The Health Insurance Portability and Accountability Act of 1996).',
            },
            {
              id: 83747,
              title:
                'How old do you have to be to buy from a dispensary in Louisiana?',
              content:
                'You must be 18 or older, have a valid recommendation from a board-certified Louisiana physician, and  have a government-issued photo ID to buy from a cannabis dispensary in Louisiana.',
            },
            {
              id: 86345,
              title:
                'Where do I go to get a medical marijuana card in Louisiana?',
              content:
                'To get a medical marijuana card in Louisiana, you must go to a healthcare provider, in-person or online, who is certified in Louisiana.',
            },
            {
              id: 86347,
              title: 'Can I get a Louisiana medical card online?',
              content:
                'Yes, you can get a Louisiana medical card online. Louisiana allows patients to use telehealth to receive certifications to get a medical card virtually. With Leafwell, your appointment could be as short as 10 minutes.',
            },
            {
              id: 86349,
              title:
                'Can I walk into a dispensary without a card in Louisiana?',
              content:
                'No, you cannot go into a Louisiana dispensary without a medical cannabis certification.\r\n\r\nLouisiana has no recreational cannabis sales, so you must be certified or a certified caregiver to enter and purchase cannabis from a dispensary.',
            },
            {
              id: 86351,
              title: 'Can I get a medical card With a felony in Louisiana?',
              content:
                'If you have one or more qualifying conditions, you can get certified for medical cannabis in Louisiana, even with a felony.',
            },
          ],
          button: '',
        },
        {
          acf_fc_layout: 'partners_location',
          instance_id: '762d0bed',
          image: null,
          title: 'Discover Our Locations Across Louisiana',
          partners: [
            {
              leafwell_scrapping_id: '',
              states: ['LA'],
              logo: null,
              utm_source: '',
              website: '',
              place_id:
                'EjA4MzIgRSBCb3N0b24gU3QgdW5pdCA2LCBDb3Zpbmd0b24sIExBIDcwNDMzLCBVU0EiIhogChYKFAoSCQMSJC00XCeGEa7-b5PQCt0lEgZ1bml0IDY',
              formatted_address:
                '832 E Boston St unit 6, Covington, LA 70433, USA',
              street_address: '',
              address_locality: '',
              address_region: '',
              postal_code: '',
              address_country: '',
              international_phone_number: '',
              latitude: '30.47666699286537',
              longitude: '-900909023',
              rating: '',
              user_ratings_total: '',
              opening_hours: '',
              price_range: '1',
              link: '/partners/covington-louisiana',
              name: 'Medical Marijuana Cards in Covington, LA',
            },
          ],
        },
        {
          acf_fc_layout: 'table_of_contents',
          instance_id: 'd732297b',
          table_of_contents: [
            {
              label: 'Testimonials',
              id: 'testimonials',
            },
            {
              label: 'Quick Steps',
              id: 'quick-steps',
            },
            {
              label: 'Benefits of Getting a Card',
              id: 'benefits-card',
            },
            {
              label: 'Cost Information',
              id: 'cost-information',
            },
            {
              label: 'Qualifying Conditions',
              id: 'qualifying-conditions',
            },
            {
              label: 'What You Need to Know',
              id: 'what-you-need-to-know',
            },
            {
              label: 'What You Need',
              id: 'what-you-need',
            },
            {
              label: 'More Questions',
              id: 'more-questions',
            },
            {
              label: 'Next steps after approval',
              id: 'state-steps',
            },
            {
              label: 'How to renew your card',
              id: 'renew',
            },
            {
              label: 'Legal limitations for LA card holders',
              id: 'laws',
            },
            {
              label: 'How to become a caregiver in Louisiana',
              id: 'caregiver',
            },
            {
              label: 'Can you use a Louisiana medical card in other states',
              id: 'reciprocity',
            },
            {
              label: 'Minor medical cannabis information',
              id: 'minor',
            },
            {
              label: 'Do I get a physical card in Louisiana?',
              id: 'card',
            },
            {
              label: 'Lousiana medical marijuana pharmacies',
              id: 'pharmacies',
            },
            {
              label: 'FAQs',
              id: 'faqs',
            },
          ],
        },
      ],
    },
    seo: {
      title:
        'How to get or renew a medical marijuana card in Louisiana - Leafwell',
      description:
        'Get or renew your LA medical marijuana card with Leafwell Our easy process will help you get access to medical cannabis in Louisiana fast.',
      canonical: 'https://leafwell.com/medical-card/louisiana',
      images: [
        {
          source_url:
            'https://admin.leafwell.com/app/uploads/2022/01/share-1024x538.jpg',
          alt_text: '',
          media_details: {
            width: 1024,
            height: 538,
          },
        },
      ],
      meta_robots_noarchive: false,
      meta_robots_nofollow: false,
      meta_robots_noindex: false,
      opengraph: {
        title:
          'How to get or renew a medical marijuana card in Louisiana - Leafwell',
        description:
          'Get or renew your LA medical marijuana card with Leafwell Our easy process will help you get access to medical cannabis in Louisiana fast.',
        type: 'website',
      },
      redirect: null,
    },
    language: 'en',
    translations: [
      {
        uri: '/medical-card/louisiana',
        slug: 'en',
        locale: 'en',
      },
      {
        uri: '/es/carta-recomendacion/louisiana',
        slug: 'es',
        locale: 'es_ES',
      },
    ],
  },
];

const PageContent: React.FC<PageProps> = ({
  page: {
    title,
    seo,
    date,
    modified,
    slug,
    acf: {
      hero,
      name,
      affiliateLogo,
      affiliateWebsite,
      formUrl,
      pauseAppointments,
      redirectMedicalCard,
      redirectProductEndpoint,
      redirectCannabisCounseling,
      description,
      imageObjectCaption,
      blocks,
      pageType,
      stateProductImage,
      product,
      totalMedicalCardPrice,
      states,
      city,
      hasReferral,
      isClinic,
    },
  },
  settings: { seoSettings },
  variant,
  featureFlag,
  inPersonEvents,
}) => {
  const posthog = usePostHog();

  useEffect(() => {
    if (featureFlag) {
      /*       posthog.capture('$feature_flag_called', {
        $feature_flag: featureFlag,
        $feature_flag_response: variant,
      }); */
      posthog.capture('$pageview', {
        experiment_page: true,
      });
    }
  }, [featureFlag, variant]);

  return (
    <PageWrap seo={seo} pageType={pageType} title={title}>
      <WebPageJson
        seo={seo}
        date={date}
        title={title}
        modified={modified}
        stateName={slug.replace(/-/g, ' ').replace(/\b\w/g, function (letter) {
          return letter.toUpperCase();
        })}
        siteUrl={seoSettings.siteUrl}
        name={name}
        description={description}
        imageObjectCaption={imageObjectCaption}
        siteDescription={seoSettings.homepage.homepageDescription}
      />
      <ProductJson
        product={product}
        totalMedicalCardPrice={totalMedicalCardPrice}
      />
      {variant === 'test' ? (
        <>
          <HeroCardNew
            hero={hero}
            states={states}
            affiliateLogo={affiliateLogo}
            pauseAppointments={pauseAppointments}
            redirectMedicalCard={redirectMedicalCard}
            inPersonEvents={inPersonEvents}
            showDoIQualify={false}
            addEmailInput={false}
            passwordless={true}
          />
          <StickyMobileCta
            observeTargetId="hero-cta-sentinel"
            stateCode={states?.[0]}
            formSubmitText={hero.formSubmitText || 'Get your card'}
          />
        </>
      ) : (
        <>
          <HeroCard
            hero={hero}
            pageType={pageType}
            states={states}
            formUrl={formUrl}
            affiliateLogo={affiliateLogo}
            affiliateWebsite={affiliateWebsite}
            pauseAppointments={pauseAppointments}
            redirectMedicalCard={redirectMedicalCard}
            redirectCannabisCounseling={redirectCannabisCounseling}
            totalMedicalCardPrice={totalMedicalCardPrice}
            city={city}
            isClinic={isClinic}
            redirectProductEndpoint={redirectProductEndpoint}
          />
          <StickyMobileCta
            observeTargetId="leafwell-signup-form"
            stateCode={states?.[0]}
            formSubmitText={hero.formSubmitText || 'Get your card'}
          />
        </>
      )}
      {blocks && blocks.length > 0 ? (
        <Blocks
          blocks={blocks}
          pageType={pageType}
          states={states}
          modified={modified}
          stateProductImage={stateProductImage}
          hasReferral={hasReferral}
          variant={variant}
        />
      ) : null}
      <MedicalReviewer modified={modified} />
    </PageWrap>
  );
};

const Page: React.FC<PageProps> = props => {
  const { isFallback } = useRouter();

  if (isFallback) {
    return <Loader />;
  }

  return <PageContent {...props} />;
};

export const getServerSideProps = (async context => {
  const { req } = context;

  const distinctId = req.headers['x-posthog-distinct-id'] as string;
  const flagKey = 'website-new-design-passwordless-LA';

  let variant: string | null = null;

  try {
    const posthog = getPostHogServerClient();
    const flags = await posthog.evaluateFlags(distinctId, {
      personProperties: { $pathname: '/medical-card/louisiana' },
    });

    const flagValue = flags.getFlag(flagKey) as string | undefined;

    if (flagValue) {
      variant = flagValue;
    } else {
      console.warn(
        `[PostHog] Flag "${flagKey}" returned undefined for ${distinctId} — excluding from experiment`,
      );
    }
  } catch (error) {
    console.warn(
      `[PostHog] evaluateFlags failed for ${distinctId}:`,
      error?.message || error,
      '— excluding from experiment',
    );
  }

  return await getPageServerSide({
    ...context,
    variant: variant as 'test' | 'control' | null,
    featureFlag: variant ? flagKey : null,
    slug: 'medical-card/louisiana',
    staticPage: camelcaseKeys(staticPage[0], { deep: true }),
    posthogBootstrap: {
      distinctID: distinctId,
      featureFlags: variant ? { [flagKey]: variant } : {},
    },
  });
}) satisfies GetServerSideProps<PageProps>;

export default Page;
